import { createFileRoute, redirect, useParams } from '@tanstack/react-router';

import { BUILDER_STEPS } from 'shared/consts';
import {
  bounceToOldPlatform,
  buildLocationDashboardPath,
  LOCATION_DASHBOARD_SEGMENT
} from 'shared/modules/old-platform-redirect';

import {
  campaignQuery,
  CampaignFlow,
  isBuilderStep,
  isEveryOrderPaid,
  useCitationBuilderStore
} from 'features/citation-builder';

export const Route = createFileRoute('/_authenticated/locations/$id/cb/campaign/$campaignId/edit/_edit')({
  parseParams: ({ campaignId }) => ({ campaignId: parseInt(campaignId, 10) }),
  stringifyParams: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  beforeLoad: async ({ context, params }) => {
    if (Number.isNaN(params.campaignId)) {
      throw redirect({ to: '/locations' });
    }

    // A paid campaign would take selections it can never save; the report is where the old
    // platform lands these customers too.
    const { campaigns } = await context.queryClient.ensureQueryData(campaignQuery(params.campaignId));

    if (isEveryOrderPaid(campaigns)) {
      return bounceToOldPlatform(buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.CITATIONS));
    }

    // The route owns seeding from the URL params, so get-started can navigate-only
    // without importing this feature. locationId comes from the `$id` path param.
    // The per-step access gate lives in the child `$step` route, which runs after
    // this seed — so it reads a fresh `maxStep` without a second `getState()`.
    const store = useCitationBuilderStore.getState();

    if (store.campaignId !== params.campaignId) {
      store.initForCampaign({ campaignId: params.campaignId, locationId: params.id });
    }
  },
  component: CampaignFlowRoute
});

function CampaignFlowRoute() {
  const { id, campaignId } = Route.useParams();
  // The active step lives on the child `$step` route; read it here in the route
  // layer (not inside the component) and narrow it to a `BuilderStep`. The child
  // route's guard already redirects invalid/inaccessible steps, so the SCAN
  // default only covers the brief pre-step render.
  const { step } = useParams({ strict: false });
  const currentStep = step && isBuilderStep(step) ? step : BUILDER_STEPS.SCAN;
  const maxStep = useCitationBuilderStore((state) => state.maxStep);

  return <CampaignFlow campaignId={campaignId} currentStep={currentStep} locationId={id} maxStep={maxStep} />;
}
